import type { AuthedRequest } from '../auth/AuthProvider';

/** Mirrors GET /api/v1/devices/{id}/recordings (ADR-012). */
export interface RecordedSegment {
  id: string;
  started_at: string;
  ended_at: string;
  duration_ms: number;
  size_bytes: number;
  profile_token: string;
  /** Where the segment is kept (ADR-035); 'node' = on its media node while the bucket is down (ADR-054); absent before 0.54. */
  storage?: 'local' | 's3' | 'node';
  /** Provider of an "s3" segment's bucket, from the list's `remote`. */
  remote?: RemoteStorage;
}

/** Cloud store behind "s3" segments: a well-known provider name, never an address. */
export interface RemoteStorage {
  /** E.g. "Backblaze B2"; "" when the provider is not a known one. */
  name: string;
}

/** Where a segment plays from, for the Playback badge; null when unknown. */
export function segmentLocation(
  s: Pick<RecordedSegment, 'storage' | 'remote'>,
): { kind: 'local' | 'cloud' | 'node'; label: string; detail: string } | null {
  if (s.storage === 'local') {
    return {
      kind: 'local',
      label: 'Disco do servidor',
      detail: 'Trecho guardado no disco do servidor',
    };
  }
  if (s.storage === 's3') {
    const name = s.remote?.name;
    return {
      kind: 'cloud',
      label: 'Nuvem',
      detail: name ? `Trecho guardado na nuvem (${name})` : 'Trecho guardado na nuvem',
    };
  }
  if (s.storage === 'node') {
    return {
      kind: 'node',
      label: 'Máquina de vídeo',
      detail: 'Trecho guardado na máquina de vídeo até subir para a nuvem',
    };
  }
  return null;
}

export interface PlaybackGrant {
  token: string;
  expires_at: string;
}

export const recordingsKey = (deviceId: string, from: number) =>
  ['recordings', deviceId, from] as const;
export const playbackKey = (deviceId: string, from: number) =>
  ['playback', deviceId, from] as const;

const isoOf = (t: number) => new Date(t).toISOString();

export async function listRecordings(
  req: AuthedRequest,
  deviceId: string,
  from: number,
  to: number,
): Promise<RecordedSegment[]> {
  const q = new URLSearchParams({
    from: isoOf(from).replace('.000Z', 'Z'),
    to: isoOf(to).replace('.000Z', 'Z'),
  });
  const body = await req<{ items: RecordedSegment[]; remote?: RemoteStorage }>(
    'GET',
    `/api/v1/devices/${encodeURIComponent(deviceId)}/recordings?${q.toString()}`,
  );
  const remote = body.remote;
  return remote ? body.items.map((s) => (s.storage === 's3' ? { ...s, remote } : s)) : body.items;
}

/** Authorises playback of [from, to) for an hour; audited as recording.playback. */
export const startPlayback = (req: AuthedRequest, deviceId: string, from: number, to: number) =>
  req<PlaybackGrant>('POST', `/api/v1/devices/${encodeURIComponent(deviceId)}/playback`, {
    from: isoOf(from).replace('.000Z', 'Z'),
    to: isoOf(to).replace('.000Z', 'Z'),
  });

/** Media URL for a <video> element: authorised by the playback token. */
export const mediaUrl = (segmentId: string, token: string) =>
  `/api/v1/recordings/${encodeURIComponent(segmentId)}/media?token=${encodeURIComponent(token)}`;
